// @ts-strict-ignore
import {
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import { Box, Button, Text } from "@saleor/macaw-ui-next";
import React from "react";

import { CarouselBanner } from "../types";

interface BannerPreviewDialogProps {
  open: boolean;
  banner: CarouselBanner | null;
  onClose: () => void;
}

export const BannerPreviewDialog: React.FC<BannerPreviewDialogProps> = ({
  open,
  banner,
  onClose,
}) => {
  if (!banner) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            Carousel Banner Storefront Preview
          </Typography>
          <IconButton size="small" onClick={onClose} title="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box display="flex" flexDirection="column" gap={4}>
          {/* Full Banner Display Container (Uncropped, Natural Dimensions) */}
          <Box
            style={{
              width: "100%",
              borderRadius: 12,
              overflow: "hidden",
              boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
              backgroundColor: "#020617",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: 8,
            }}
          >
            {banner.imageUrl ? (
              <img
                src={banner.imageUrl}
                alt={banner.altText || banner.title}
                style={{
                  maxWidth: "100%",
                  maxHeight: "60vh",
                  width: "auto",
                  height: "auto",
                  objectFit: "contain",
                  borderRadius: 8,
                  display: "block",
                }}
              />
            ) : (
              <Box padding={6}>
                <Text color="default2">No banner image uploaded</Text>
              </Box>
            )}
          </Box>

          {/* Banner Details & Storefront Info Card */}
          <Box
            padding={3}
            style={{
              backgroundColor: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: 8,
              display: "flex",
              flexDirection: "column",
              gap: 16,
            }}
          >
            <Box display="flex" flexDirection="column" gap={1}>
              {banner.subtitle && (
                <Typography
                  variant="overline"
                  style={{ color: "#16a34a", fontWeight: 700, letterSpacing: 1.2 }}
                >
                  {banner.subtitle}
                </Typography>
              )}
              <Typography variant="h5" style={{ fontWeight: 700, color: "#1e293b" }}>
                {banner.title}
              </Typography>
              {banner.description && (
                <Typography variant="body2" color="textSecondary" style={{ maxWidth: 700 }}>
                  {banner.description}
                </Typography>
              )}
            </Box>

            <Box
              display="grid"
              style={{ gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}
              gap={2}
            >
              <Box display="flex" flexDirection="column" gap={0.5}>
                <Typography variant="caption" color="textSecondary">
                  Status
                </Typography>
                <Box>
                  <Chip
                    size="small"
                    label={banner.isPublished ? "Published (Active)" : "Draft (Hidden)"}
                    style={{
                      backgroundColor: banner.isPublished ? "#dcfce7" : "#fee2e2",
                      color: banner.isPublished ? "#15803d" : "#b91c1c",
                      fontWeight: 600,
                    }}
                  />
                </Box>
              </Box>

              <Box display="flex" flexDirection="column" gap={0.5} style={{ minWidth: 0 }}>
                <Typography variant="caption" color="textSecondary">
                  Redirection Action
                </Typography>
                <Typography
                  variant="body2"
                  style={{
                    fontWeight: 600,
                    wordBreak: "break-all",
                    overflowWrap: "anywhere",
                  }}
                >
                  {banner.redirectType}: {banner.redirectValue || "None"}
                </Typography>
              </Box>

              <Box display="flex" flexDirection="column" gap={0.5}>
                <Typography variant="caption" color="textSecondary">
                  Target Platform
                </Typography>
                <Box>
                  <Chip
                    size="small"
                    label={banner.targetPlatform === "APP" ? "Mobile App" : banner.targetPlatform === "WEB" ? "Web App" : "All Platforms"}
                    style={{ backgroundColor: "#e0f2fe", color: "#0369a1", fontWeight: 600 }}
                  />
                </Box>
              </Box>

              <Box display="flex" flexDirection="column" gap={0.5}>
                <Typography variant="caption" color="textSecondary">
                  Target Device
                </Typography>
                <Box>
                  <Chip
                    size="small"
                    label={banner.targetDevice === "MOBILE" ? "Mobile" : banner.targetDevice === "TABLET" ? "Tablet" : banner.targetDevice === "DESKTOP" ? "Desktop" : "All Devices"}
                    style={{ backgroundColor: "#f3e8ff", color: "#7e22ce", fontWeight: 600 }}
                  />
                </Box>
              </Box>

              <Box display="flex" flexDirection="column" gap={0.5}>
                <Typography variant="caption" color="textSecondary">
                  Display Sort Order
                </Typography>
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  Position #{banner.sortOrder ?? 0}
                </Typography>
              </Box>

              {banner.imageUrl && (
                <Box display="flex" flexDirection="column" gap={0.5}>
                  <Typography variant="caption" color="textSecondary">
                    Direct Image Asset
                  </Typography>
                  <a
                    href={banner.imageUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      color: "#2563eb",
                      backgroundColor: "#eff6ff",
                      border: "1px solid #bfdbfe",
                      borderRadius: 6,
                      padding: "6px 12px",
                      fontSize: "0.95rem",
                      fontWeight: 600,
                      textDecoration: "none",
                      width: "fit-content",
                    }}
                  >
                    <span>Open Full Image</span>
                    <OpenInNewIcon style={{ fontSize: 18 }} />
                  </a>
                </Box>
              )}
            </Box>
          </Box>

        </Box>
      </DialogContent>

      <DialogActions style={{ padding: "12px 24px" }}>
        <Button variant="secondary" onClick={onClose}>
          Close Preview
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BannerPreviewDialog;
